import { Injectable } from '@angular/core';
import { MembersService, PushService } from '@careapp-group/ng-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { FrontendApi } from '@ory/client';
import { from, of as observableOf } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { State } from '../../reducers';
import * as actions from './settings.actions';
import { getSettingsFlow } from './settings.selectors';
import { auth as authSelectors } from '../auth.selectors';
import { kratosErrorHandler } from '../kratos-error-handler';

export interface KratosError {
  error: {
    id: string;
    code: number;
    status: string;
    reason: string;
    message: string;
  };
  redirect_browser_to?: string;
}

@Injectable()
export class AuthSettingsEffects {
  createSettingsFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestCreateSettingsFlow),
      switchMap((action) =>
        from(this.kratos.createBrowserSettingsFlow()).pipe(
          map((res) => actions.SuccessCreateSettingsFlow({ flow: res.data })),
          catchError((err) => observableOf(actions.FailCreateSettingsFlow({ error: err.response })))
        )
      )
    )
  );

  getSettingsFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestGetSettingsFlow),
      switchMap((action) =>
        from(this.kratos.getSettingsFlow({ id: action.flowId })).pipe(
          map((res) => actions.SuccessCreateSettingsFlow({ flow: res.data })),
          catchError((err) => observableOf(actions.FailCreateSettingsFlow({ error: err.response })))
        )
      )
    )
  );

  updateSettingsFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestUpdateSettingsFlow),
      withLatestFrom(this.store.select(getSettingsFlow)),
      switchMap(([action, flow]) =>
        from(
          this.kratos.updateSettingsFlow({
            flow: flow.id,
            updateSettingsFlowBody: <any>{
              ...action.body,
              csrf_token: flow.ui.nodes.find((n) => n.attributes['name'] === 'csrf_token')
                .attributes['value'],
            },
          })
        ).pipe(
          map((res) => actions.SuccessUpdateSettingsFlow({ flow: res.data })),
          catchError((error) =>
            observableOf(
              kratosErrorHandler({
                error,
                redirect: `/profile?flow=${flow.id}&section=${action.body.method}`,
              }) ?? actions.FailUpdateSettingsFlow({ error: 'o dear', flow: error.response.data })
            )
          )
        )
      )
    )
  );

  changePassword = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestChangePasswordAction),
      withLatestFrom(this.store.select(authSelectors.getPasswordChangeFlowId)),
      switchMap(([action, flowId]) =>
        from(
          flowId
            ? this.kratos
                .getSettingsFlow({
                  id: flowId,
                })
                .then((flow) =>
                  this.kratos.updateSettingsFlow({
                    flow: flow.data.id,
                    updateSettingsFlowBody: {
                      csrf_token: flow.data.ui.nodes.find(
                        (n) => n.attributes['name'] === 'csrf_token'
                      ).attributes['value'],
                      method: 'password',
                      password: action.password,
                    },
                  })
                )
            : this.kratos.createBrowserSettingsFlow().then((flow) =>
                this.kratos.updateSettingsFlow({
                  flow: flow.data.id,
                  updateSettingsFlowBody: {
                    csrf_token: flow.data.ui.nodes.find(
                      (n) => n.attributes['name'] === 'csrf_token'
                    ).attributes['value'],
                    method: 'password',
                    password: action.password,
                  },
                })
              )
        ).pipe(
          map((res) => {
            return actions.ResponseSuccessChangePasswordAction({
              payload: res.data,
            });
          }),
          catchError((err) => {
            const error = err.response.data.ui.nodes.filter(
              (n) => n.group === 'password' && n.messages?.length > 0
            )[0].messages[0].text;
            return observableOf(
              actions.ResponseFailChangePasswordAction({
                error: error,
              })
            );
          })
        )
      )
    )
  );

  constructor(
    private store: Store<State>,
    private actions$: Actions,
    private kratos: FrontendApi
  ) {}
}
